<template>
  <div class="mx-0 home__banner-slider home__banner-slider-first" id="home__banner-slider-first">
    <div v-if="banner" class="top_banner position-relative">
      <a class="inner_top_banner" :href="banner.cta?.cta_link || '#'" target="_self"
        :aria-label="banner.cta?.cta_label || banner.cta?.cta_text || 'Shop now'">
        <!-- A <picture> lets the preload scanner request only the mobile or
             desktop image. Previously both eager, high-priority <img> tags
             could compete for the first mobile connection. -->
        <picture v-if="!isPcVideo && !isMbVideo" class="responsive-banner">
          <source v-if="pcPoster" media="(min-width: 1000px)" :srcset="pcPoster" width="1654" height="517" />
          <source v-if="mbPoster" media="(max-width: 999px)" :srcset="mbPoster" width="900" height="840" />
          <img class="responsive-banner__image" :src="mbPoster || pcPoster"
            :alt="banner.mb_image?.alt || banner.img_pc?.alt || banner.title_heading || 'banner'"
            width="900" height="840" loading="eager" fetchpriority="high" @load="onOriginalImageLoad($event)" />
        </picture>

        <template v-else>
          <template v-if="isPcVideo">
            <video ref="pcVideoRef" class="video_banner pc_banner" :id="`video_banner_pc_${banner.id}`"
              :poster="isPcVideoLoaded ? undefined : pcPoster" width="1654" height="517" autoplay muted playsinline loop preload="none"
              @canplay="onPcVideoReady" @playing="onPcVideoReady">
              <source v-if="pcSourceLoaded" :src="banner.pc_video?.video_url_pc" type="video/mp4" />
            </video>
            <img class="pc_banner banner_poster_img" :class="{ 'poster-hidden': isPcVideoLoaded }" :src="pcPoster"
              :alt="banner.img_pc?.alt || banner.title_heading || 'banner'" width="1654" height="517" loading="eager"
              fetchpriority="high" @load="startVideoLoading" />
          </template>
          <img v-else class="pc_banner" :src="pcPoster" :alt="banner.img_pc?.alt || banner.title_heading || 'banner'"
            width="1654" height="517" loading="eager" fetchpriority="high" @load="onOriginalImageLoad($event, 'pc')" />

          <template v-if="isMbVideo">
            <video ref="mbVideoRef" class="video_banner mobile_banner" :id="`video_banner_mb_${banner.id}`"
              :poster="isMbVideoLoaded ? undefined : mbPoster" width="900" height="840" autoplay muted playsinline loop preload="none"
              @canplay="onMbVideoReady" @playing="onMbVideoReady">
              <source v-if="mbSourceLoaded" :src="banner.mb_video?.video_url_mb" type="video/mp4" />
            </video>
            <img class="mobile_banner banner_poster_img" :class="{ 'poster-hidden': isMbVideoLoaded }" :src="mbPoster"
              :alt="banner.mb_image?.alt || banner.title_heading || 'banner'" width="900" height="840" loading="eager"
              fetchpriority="high" @load="startVideoLoading" />
          </template>
          <img v-else class="mobile_banner" :src="mbPoster" :alt="banner.mb_image?.alt || banner.title_heading || 'banner'"
            width="900" height="840" loading="eager" fetchpriority="high" @load="onOriginalImageLoad($event, 'mb')" />
        </template>
      </a>
    </div>
  </div>
</template>

<script setup lang="ts">
const props = defineProps({
  pageSluga: { type: String, default: '' },
  // Supplied by Home/Index during SSR so the LCP poster is already in HTML.
  initialBanner: { type: Object, default: null },
});
const emit = defineEmits(['media-ready']);
const { $file } = useNuxtApp();
const userStore = useUserStore();
const remoteBanner = ref<any>(null);
const canLoadVideo = ref(false);
const isPcVideoLoaded = ref(false);
const isMbVideoLoaded = ref(false);
const pcVideoRef = ref<HTMLVideoElement | null>(null);
const mbVideoRef = ref<HTMLVideoElement | null>(null);
const pcSourceLoaded = ref(false);
const mbSourceLoaded = ref(false);
const isMobileViewport = ref(false);
const loadedVideoSources = new WeakMap<HTMLVideoElement, string>();
const pendingVideoPlays = new WeakSet<HTMLVideoElement>();
let idleLoadId: number | undefined;
let idleLoadTimeoutId: number | undefined;

const banner = computed(() => props.initialBanner || remoteBanner.value);
const isPcVideo = computed(() => Boolean(
  banner.value?.pc_video?.video_url_pc &&
  (banner.value?.image_banner_item_type_pc === 'video' || banner.value?.image_banner_item_type === 'video'),
));
const isMbVideo = computed(() => Boolean(
  banner.value?.mb_video?.video_url_mb &&
  (banner.value?.image_banner_item_type_mb === 'video' || banner.value?.image_banner_item_type === 'video'),
));
const pcPoster = computed(() => banner.value?.pc_video?.full || banner.value?.img_pc?.full || banner.value?.pc_video?.lazy || banner.value?.img_pc?.lazy);
const mbPoster = computed(() => banner.value?.mb_video?.full || banner.value?.mb_image?.full || banner.value?.mb_video?.lazy || banner.value?.mb_image?.lazy);

const startVideoLoading = () => { canLoadVideo.value = true; };
// Once a video has played, scrolling must not show its initial poster again.
const onPcVideoReady = () => {
  if (pcVideoRef.value && !pcVideoRef.value.paused && pcVideoRef.value.readyState >= 2) {
    isPcVideoLoaded.value = true;
    if (!isMobileViewport.value) emit('media-ready');
  }
};
const onMbVideoReady = () => {
  if (mbVideoRef.value && !mbVideoRef.value.paused && mbVideoRef.value.readyState >= 2) {
    isMbVideoLoaded.value = true;
    if (isMobileViewport.value) emit('media-ready');
  }
};

const onOriginalImageLoad = (event: Event, viewport?: 'pc' | 'mb') => {
  if (viewport && viewport !== (isMobileViewport.value ? 'mb' : 'pc')) return;
  if ((event.target as HTMLImageElement).naturalWidth) emit('media-ready');
};

const pauseVideos = () => {
  pcVideoRef.value?.pause();
  mbVideoRef.value?.pause();
};

const syncVideoPlayback = async () => {
  if (!canLoadVideo.value || document.hidden) {
    pauseVideos();
    return;
  }

  if (isMobileViewport.value) pcVideoRef.value?.pause();
  else mbVideoRef.value?.pause();
  const sourceLoaded = isMobileViewport.value ? mbSourceLoaded : pcSourceLoaded;
  sourceLoaded.value = true;
  await nextTick();
  if (!canLoadVideo.value || document.hidden) return;

  const video = isMobileViewport.value ? mbVideoRef.value : pcVideoRef.value;
  if (!video || pendingVideoPlays.has(video)) return;
  const source = video.querySelector('source')?.src;
  if (!source) return;
  if (loadedVideoSources.get(video) !== source) {
    if (video === pcVideoRef.value) {
      if (!isPcVideoLoaded.value) isPcVideoLoaded.value = false;
    } else {
      if (!isMbVideoLoaded.value) isMbVideoLoaded.value = false;
    }
    loadedVideoSources.set(video, source);
    video.load();
  }
  if (!video.paused) return;
  video.muted = true;
  pendingVideoPlays.add(video);
  try {
    await video.play();
  } catch {
    // Keep the initial poster, or the existing video frame after first playback.
  } finally {
    pendingVideoPlays.delete(video);
  }
};

const updateVideoViewport = () => {
  isMobileViewport.value = window.matchMedia('(max-width: 999px)').matches;
};

watch([canLoadVideo, isMobileViewport], syncVideoPlayback, { flush: 'post' });

onMounted(() => {
  updateVideoViewport();
  window.addEventListener('resize', updateVideoViewport);
  document.addEventListener('visibilitychange', syncVideoPlayback);
  window.addEventListener('pageshow', syncVideoPlayback);
  window.addEventListener('pagehide', pauseVideos);
  // Keep the poster as the LCP element; defer the video request until idle.
  if ('requestIdleCallback' in window) idleLoadId = window.requestIdleCallback(startVideoLoading, { timeout: 2000 });
  else idleLoadTimeoutId = window.setTimeout(startVideoLoading, 1500);
});

onBeforeUnmount(() => {
  canLoadVideo.value = false;
  pauseVideos();
  window.removeEventListener('resize', updateVideoViewport);
  document.removeEventListener('visibilitychange', syncVideoPlayback);
  window.removeEventListener('pageshow', syncVideoPlayback);
  window.removeEventListener('pagehide', pauseVideos);
  if (idleLoadId !== undefined && 'cancelIdleCallback' in window) window.cancelIdleCallback(idleLoadId);
  if (idleLoadTimeoutId !== undefined) window.clearTimeout(idleLoadTimeoutId);
});

if (!props.initialBanner) {
  const { data } = await useFetch<{ success: boolean; item: any }>('/api/home-banner-first', {
    key: `first-banner-${props.pageSluga || 'en'}`,
    query: props.pageSluga ? { lang: props.pageSluga } : undefined,
    server: true,
    lazy: false,
  });
  remoteBanner.value = data.value?.item || null;

  // Some deployments keep CMS JSON on the API host rather than the Nuxt host.
  // Retain the existing authenticated route as a reliable SSR fallback.
  if (!remoteBanner.value) {
    let fileName = 'sksg_home_first.json';
    if (props.pageSluga) fileName = `${props.pageSluga}_${fileName}`;

    const { data: legacyData } = await useAsyncData(`first-banner-file-${props.pageSluga || 'en'}`, () =>
      $file.get(`home/${fileName}`, {
        headers: {
          Authorization: `Bearer ${userStore.csrf}`,
          'X-API-Server': userStore.csrf_server,
        },
      }),
    );
    remoteBanner.value = legacyData.value?.items?.[0] || null;
  }
}

useHead(() => {
  if (!banner.value) return {};
  const link: Array<Record<string, string | number>> = [];
  // Place image hints after charset/viewport but before the large inline styles.
  if (pcPoster.value) link.push({ key: 'first-banner-pc', tagPriority: -1, rel: 'preload', as: 'image', href: pcPoster.value, media: '(min-width: 1000px)', fetchpriority: 'high' });
  if (mbPoster.value) link.push({ key: 'first-banner-mb', tagPriority: -1, rel: 'preload', as: 'image', href: mbPoster.value, media: '(max-width: 999px)', fetchpriority: 'high' });
  return { link };
});

defineExpose({
  pauseVideos,
  syncVideoPlayback,
  isPcVideoLoaded,
  isMbVideoLoaded,
});
</script>

<style scoped lang="scss">
.inner_top_banner {
  position: relative;
  display: block;

  .video_banner {
    position: absolute;
    inset: 0;
    z-index: 1;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .banner_poster_img {
    position: relative;
    z-index: 2;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: opacity .3s ease, visibility .3s ease;

    &.poster-hidden {
      opacity: 0;
      visibility: hidden;
      pointer-events: none;
    }
  }
}

.responsive-banner {
  display: block;

  &__image {
    display: block;
    width: 100%;
    height: auto;
  }
}

@media (min-width: 1000px) {
  .mobile_banner { display: none !important; }
}

@media (max-width: 999px) {
  .pc_banner { display: none !important; }
  .home__banner-slider-first .inner_top_banner > .mobile_banner {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top;
  }
  .responsive-banner__image {
    height: 100%;
    object-fit: cover;
    object-position: top;
  }
}

body:has(.cs_announ_nomal) .sk-home__nuxt .home__banner-slider-first {
  @media (min-width: 641px) and (max-width: 999px) { height: calc(100vh - 170px); }
  @media screen and (max-width: 640px) { height: calc(100vh - 126px); }
  @media screen and (max-width: 575px) { height: calc(100vh - 255px); }
  @media screen and (max-width: 480px) { height: calc(100vh - 315px); }
}

body:has(.cs_announ_slide) .sk-home__nuxt .home__banner-slider-first {
  @media (min-width: 641px) and (max-width: 999px) { height: calc(100vh - 135px); }
  @media screen and (max-width: 640px) { height: calc(100vh - 126px); }
  @media screen and (max-width: 575px) { height: calc(100vh - 315px); }
}
</style>
